// src/utils/offerImport.js
// Offres lues dans un PDF de galerie (réponse de /extract-offers) : transformation en fiche d'œuvre, règles de prix.
// Fonctions pures, sans React ni base : testables seules.
// Principe : une proposition n'est pas une propriété. Le prix de la galerie est conservé tel quel, à part ;
// le prix montré au client (HT ou TTC) est saisi ou validé par le conseiller, jamais calculé.

export const PRICE_MODES = ["none", "incl", "excl"];

// Clé stable d'une offre dans une proposition : référence de la galerie, sinon artiste + titre + année.
export function offerKey(o) {
  if (o?.external_reference) return `ref:${String(o.external_reference).trim().toLowerCase()}`;
  const n = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return `id:${n(o?.artist)}|${n(o?.title)}|${n(o?.year)}`;
}

// Texte des dimensions « H x L x P cm » à partir des valeurs lues ; à défaut, le texte brut du document.
export function dimensionsText(o) {
  const d = o?.dimensions_cm;
  if (d && d.height != null && d.width != null) {
    return [d.height, d.width, d.depth].filter((v) => v != null).join(" x ") + " cm";
  }
  return o?.dimensions_raw || null;
}

// Ligne `artworks` d'une offre retenue. Rien d'acquisition : ni prix payé, ni estimation, ni statut de détention.
export function offerToArtwork(o, { clientId, collectionId, imageUrl = null, issuer = null, filename = null }) {
  const d = o?.dimensions_cm;
  const row = {
    client_id: clientId,
    collection_id: collectionId,
    title: (o?.title || "").trim() || "Sans titre",
    artist: o?.artist || null,
    year: o?.year || null,
    medium: o?.medium || null,
    dimensions: dimensionsText(o),
    editionInfo: o?.edition || null,
    external_source: `pdf_offer:${collectionId}`,
    external_id: o?.external_reference ? String(o.external_reference).trim() : offerKey(o),
    source_collection: issuer || filename || null,
    imported_at: new Date().toISOString(),
  };
  if (d && d.height != null && d.width != null) {
    Object.assign(row, { dimensions_height: d.height, dimensions_width: d.width, dimensions_depth: d.depth ?? null, dimensions_unit: "cm" });
  }
  if (imageUrl) {
    row.image_url = imageUrl;
    row.images = [{ id: `img_${Date.now()}_${Math.random().toString(36).slice(2)}`, path: imageUrl, url: imageUrl, publicUrl: imageUrl, isPrimary: true, filename: filename || "offre.jpg" }];
  }
  return row;
}

// Ligne `proposal_offers` : la source, la page, le prix de la galerie et la mention d'origine, tels que lus.
export function offerToRecord(o, { collectionId, advisorId, source }) {
  const p = o?.price || {};
  return {
    collection_id: collectionId,
    advisor_id: advisorId,
    source_sha256: source?.sha256 || null,
    source_filename: source?.filename || null,
    source_pages: o?.pages || null,
    issuer_hint: source?.issuer_hint || null,
    external_reference: o?.external_reference || null,
    price_status: p.status || "not_stated",
    price_amount: p.amount ?? null,
    price_currency: p.currency || null,
    price_tax: p.tax || null,
    price_raw: p.raw || null,
    extractor_version: source?.extractor_version || null,
    warnings: o?.warnings || [],
    raw: { artist_raw: o?.artist_raw ?? null, source_text: o?.source_text ?? null },
  };
}

// Montant validé pour le mode choisi, ou null s'il manque.
export function priceForMode(item, mode) {
  if (mode === "incl") return item?.price_incl ?? null;
  if (mode === "excl") return item?.price_excl ?? null;
  return null;
}

// Œuvres dont le prix du mode choisi n'est pas validé : à compléter avant de partager « avec prix ».
export function missingPrices(items, mode) {
  if (mode !== "incl" && mode !== "excl") return [];
  return (items || []).filter((i) => priceForMode(i, mode) == null);
}

// Prix de la galerie proposé comme point de départ, seulement si la mention du document est claire.
export function galleryPriceHint(record) {
  if (!record || record.price_status !== "amount" || record.price_amount == null) return null;
  return {
    amount: Number(record.price_amount),
    currency: record.price_currency || null,
    tax: record.price_tax || null, // « excl » (HT), « incl » (TTC) ou null (non précisé : à confirmer par le conseiller)
  };
}

// Offres sélectionnées par défaut : tout, sauf ce qui demande une vérification sévère.
export function defaultSelection(offers) {
  return new Set((offers || []).filter((o) => !o.needs_review).map(offerKey));
}
